import React, { useState, useEffect } from 'react';
import apiService, { formatHelpers, errorHelpers, authHelpers } from '../../../utils/apiService';

const AdminOverview = ({ analytics, loading }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState({
    database: { status: 'healthy', uptime: '99.9%', description: 'Running smoothly' },
    email: { status: 'healthy', uptime: '100%', description: 'All emails delivered' },
    payment: { status: 'healthy', uptime: '99.8%', description: 'Processing normally' },
    storage: { status: 'warning', uptime: '85%', description: '85% capacity used' }
  });
  const [error, setError] = useState('');

  // Get current user role for data filtering
  const currentUser = authHelpers.getCurrentUser();
  const isSuperAdmin = authHelpers.isSuperAdmin();

  useEffect(() => {
    loadRecentActivity();
    checkSystemHealth();
  }, []);

  const loadRecentActivity = async () => {
    try {
      setActivityLoading(true);
      setError('');

      // Fetch recent activity logs (last 10 activities)
      const response = await apiService.admin.getActivityLogs({
        page: 1,
        per_page: 10
      });

      // Transform activity logs to match the component's expected format
      const transformedActivity = response.data.logs.map(log => ({
        id: log.id,
        type: getActivityTypeFromLog(log.activity),
        description: formatActivityDescription(log),
        user: log.user_email || 'System',
        timestamp: log.timestamp,
        status: getActivityStatus(log.activity)
      }));

      setRecentActivity(transformedActivity);
    } catch (err) {
      console.error('Error loading recent activity:', err);
      setError(errorHelpers.getErrorMessage(err));
      // Fallback to empty array if API fails
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const checkSystemHealth = async () => {
    try {
      // Check API health
      await apiService.health();

      // Update system health status based on API availability
      setSystemHealth(prev => ({
        ...prev,
        database: { ...prev.database, status: 'healthy' },
        email: { ...prev.email, status: 'healthy' }
      }));
    } catch (err) {
      console.error('System health check failed:', err);
      setSystemHealth(prev => ({
        ...prev,
        database: { ...prev.database, status: 'error', description: 'Connection issues' }
      }));
    }
  };

  const getActivityTypeFromLog = (activity) => {
    const activityLower = activity.toLowerCase();

    if (activityLower.includes('verification') || activityLower.includes('certificate')) {
      return 'verification';
    } else if (activityLower.includes('upload') || activityLower.includes('certificate')) {
      return 'upload';
    } else if (activityLower.includes('payment') || activityLower.includes('payment')) {
      return 'payment';
    } else if (activityLower.includes('user') || activityLower.includes('registration') || activityLower.includes('login')) {
      return 'user';
    } else if (activityLower.includes('admin')) {
      return 'admin';
    }
    return 'other';
  };

  const formatActivityDescription = (log) => {
    // Clean up the activity description for better readability
    let description = log.activity;

    if (log.details) {
      // If details exist, create a more descriptive message
      if (log.activity.includes('Certificate Verification')) {
        return `Certificate verification: ${log.details}`;
      } else if (log.activity.includes('Payment')) {
        return `Payment activity: ${log.details}`;
      } else if (log.activity.includes('Upload')) {
        return `Upload activity: ${log.details}`;
      } else if (log.activity.includes('User')) {
        return `User activity: ${log.details}`;
      }

      // Truncate long details
      const truncatedDetails = formatHelpers.truncateText(log.details, 50);
      return `${description}: ${truncatedDetails}`;
    }

    return description;
  };

  const getActivityStatus = (activity) => {
    const activityLower = activity.toLowerCase();

    if (activityLower.includes('success') || activityLower.includes('completed') || activityLower.includes('verified')) {
      return 'success';
    } else if (activityLower.includes('failed') || activityLower.includes('error') || activityLower.includes('not found')) {
      return 'failed';
    } else if (activityLower.includes('warning') || activityLower.includes('pending')) {
      return 'warning';
    } else if (activityLower.includes('login') || activityLower.includes('registration') || activityLower.includes('created')) {
      return 'info';
    }
    return 'info';
  };

  if (loading) {
    return (
      <div className="admin-overview">
        <div className="overview-grid">
          <div className="overview-card loading">
            <div className="loading-skeleton"></div>
          </div>
          <div className="overview-card loading">
            <div className="loading-skeleton"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="admin-overview">
        <div className="no-data">
          <span className="no-data-icon">📊</span>
          <h3>No Analytics Data Available</h3>
          <p>Analytics data will appear here once the system has some activity.</p>
        </div>
      </div>
    );
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'verification': return '🔍';
      case 'upload': return '📤';
      case 'user': return '👤';
      case 'payment': return '💳';
      case 'admin': return '⚙️';
      default: return '📋';
    }
  };

  const getActivityStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const calculateGrowthRate = (recent, total) => {
    if (total === 0) return 0;
    return ((recent / total) * 100).toFixed(1);
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return '❓';
    }
  };

  const getHealthStatusClass = (status) => {
    switch (status) {
      case 'healthy': return 'healthy';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'unknown';
    }
  };

  return (
    <div className="admin-overview">
      <div className="overview-header">
        <h2 className="overview-title">📊 System Overview</h2>
        <p className="overview-subtitle">
          Real-time insights into system performance and activity
          {!isSuperAdmin && <span className="access-note"> (Admin View)</span>}
        </p>
      </div>

      {error && (
        <div className="alert alert-warning">
          ⚠️ Some data may be limited due to: {error}
        </div>
      )}

      {/* Key Metrics Summary */}
      <div className="metrics-summary">
        <div className="summary-card primary">
          <div className="summary-content">
            <div className="summary-number">
              {formatHelpers.formatNumber(analytics.total_authentications)}
            </div>
            <div className="summary-label">Total Verifications</div>
            <div className="summary-change positive">
              +{analytics.recent_authentications} this month
            </div>
          </div>
          <div className="summary-icon">🔍</div>
        </div>

        <div className="summary-card success">
          <div className="summary-content">
            <div className="summary-number">
              {formatHelpers.formatCurrency(analytics.total_revenue)}
            </div>
            <div className="summary-label">Total Revenue</div>
            <div className="summary-change positive">
              +{formatHelpers.formatCurrency(analytics.recent_revenue)} this month
            </div>
          </div>
          <div className="summary-icon">💰</div>
        </div>

        <div className="summary-card info">
          <div className="summary-content">
            <div className="summary-number">
              {analytics.success_rate}%
            </div>
            <div className="summary-label">Success Rate</div>
            <div className="summary-change neutral">
              Overall verification success
            </div>
          </div>
          <div className="summary-icon">📈</div>
        </div>
      </div>

      <div className="overview-grid">
        {/* Recent Activity */}
        <div className="overview-card">
          <div className="card-header">
            <h3 className="card-title">🕒 Recent Activity</h3>
            <span className="activity-count">
              {activityLoading ? 'Loading...' : `${recentActivity.length} recent`}
            </span>
          </div>
          <div className="card-body">
            {activityLoading ? (
              <div className="loading-activity">
                <div className="loading-skeleton-small"></div>
                <div className="loading-skeleton-small"></div>
                <div className="loading-skeleton-small"></div>
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="activity-list">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="activity-content">
                      <div className="activity-description">
                        {activity.description}
                      </div>
                      <div className="activity-meta">
                        <span className="activity-user">{activity.user}</span>
                        <span className="activity-time">
                          {formatHelpers.formatDateTime(activity.timestamp)}
                        </span>
                      </div>
                    </div>
                    <div className={`activity-status ${getActivityStatusColor(activity.status)}`}>
                      {activity.status === 'success' && '✅'}
                      {activity.status === 'failed' && '❌'}
                      {activity.status === 'warning' && '⚠️'}
                      {activity.status === 'info' && 'ℹ️'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-activity">
                <span className="no-activity-icon">📝</span>
                <p>No recent activity to display</p>
              </div>
            )}
            <div className="activity-footer">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => window.location.hash = '#/admin/activity-logs'}
              >
                View All Activity
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="overview-card">
          <div className="card-header">
            <h3 className="card-title">📊 Quick Statistics</h3>
            <span className="stats-period">Last 30 days</span>
          </div>
          <div className="card-body">
            <div className="quick-stats">
              <div className="quick-stat">
                <div className="quick-stat-icon">👥</div>
                <div className="quick-stat-content">
                  <div className="quick-stat-number">{formatHelpers.formatNumber(analytics.recent_users)}</div>
                  <div className="quick-stat-label">New Users</div>
                  <div className="quick-stat-total">
                    of {formatHelpers.formatNumber(analytics.total_users)} total
                  </div>
                </div>
                <div className="quick-stat-growth">
                  +{calculateGrowthRate(analytics.recent_users, analytics.total_users)}%
                </div>
              </div>

              <div className="quick-stat">
                <div className="quick-stat-icon">📜</div>
                <div className="quick-stat-content">
                  <div className="quick-stat-number">{formatHelpers.formatNumber(analytics.total_certificates)}</div>
                  <div className="quick-stat-label">Certificates</div>
                  <div className="quick-stat-total">
                    in database
                  </div>
                </div>
                <div className="quick-stat-growth">
                  100%
                </div>
              </div>

              <div className="quick-stat">
                <div className="quick-stat-icon">💳</div>
                <div className="quick-stat-content">
                  <div className="quick-stat-number">{formatHelpers.formatNumber(analytics.recent_authentications)}</div>
                  <div className="quick-stat-label">Verifications</div>
                  <div className="quick-stat-total">
                    this month
                  </div>
                </div>
                <div className="quick-stat-growth">
                  +{calculateGrowthRate(analytics.recent_authentications, analytics.total_authentications)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Indicators */}
        <div className="overview-card">
          <div className="card-header">
            <h3 className="card-title">🎯 Performance Indicators</h3>
          </div>
          <div className="card-body">
            <div className="performance-indicators">
              <div className="indicator">
                <div className="indicator-header">
                  <span className="indicator-label">Verification Success Rate</span>
                  <span className="indicator-value">{analytics.success_rate}%</span>
                </div>
                <div className="indicator-bar">
                  <div
                    className={`indicator-fill ${analytics.success_rate >= 80 ? 'excellent' : analytics.success_rate >= 60 ? 'good' : 'poor'}`}
                    style={{ width: `${analytics.success_rate}%` }}
                  ></div>
                </div>
                <div className="indicator-description">
                  {analytics.success_rate >= 80 ? 'Excellent performance' :
                    analytics.success_rate >= 60 ? 'Good performance' : 'Needs improvement'}
                </div>
              </div>

              <div className="indicator">
                <div className="indicator-header">
                  <span className="indicator-label">User Growth</span>
                  <span className="indicator-value">
                    +{calculateGrowthRate(analytics.recent_users, analytics.total_users)}%
                  </span>
                </div>
                <div className="indicator-bar">
                  <div
                    className="indicator-fill good"
                    style={{ width: `${Math.min(calculateGrowthRate(analytics.recent_users, analytics.total_users), 100)}%` }}
                  ></div>
                </div>
                <div className="indicator-description">
                  Monthly user acquisition rate
                </div>
              </div>

              <div className="indicator">
                <div className="indicator-header">
                  <span className="indicator-label">Revenue Growth</span>
                  <span className="indicator-value">
                    +{calculateGrowthRate(analytics.recent_revenue, analytics.total_revenue)}%
                  </span>
                </div>
                <div className="indicator-bar">
                  <div
                    className="indicator-fill excellent"
                    style={{ width: `${Math.min(calculateGrowthRate(analytics.recent_revenue, analytics.total_revenue), 100)}%` }}
                  ></div>
                </div>
                <div className="indicator-description">
                  Monthly revenue growth trend
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="overview-card">
          <div className="card-header">
            <h3 className="card-title">🏥 System Health</h3>
            <span className={`health-status ${getHealthStatusClass('healthy')}`}>
              All Systems Operational
            </span>
          </div>
          <div className="card-body">
            <div className="health-indicators">
              <div className="health-item">
                <div className={`health-icon ${getHealthStatusClass(systemHealth.database.status)}`}>
                  {getHealthStatusIcon(systemHealth.database.status)}
                </div>
                <div className="health-content">
                  <div className="health-label">Database</div>
                  <div className="health-description">{systemHealth.database.description}</div>
                </div>
                <div className="health-uptime">{systemHealth.database.uptime}</div>
              </div>

              <div className="health-item">
                <div className={`health-icon ${getHealthStatusClass(systemHealth.email.status)}`}>
                  {getHealthStatusIcon(systemHealth.email.status)}
                </div>
                <div className="health-content">
                  <div className="health-label">Email Service</div>
                  <div className="health-description">{systemHealth.email.description}</div>
                </div>
                <div className="health-uptime">{systemHealth.email.uptime}</div>
              </div>

              <div className="health-item">
                <div className={`health-icon ${getHealthStatusClass(systemHealth.payment.status)}`}>
                  {getHealthStatusIcon(systemHealth.payment.status)}
                </div>
                <div className="health-content">
                  <div className="health-label">Payment System</div>
                  <div className="health-description">{systemHealth.payment.description}</div>
                </div>
                <div className="health-uptime">{systemHealth.payment.uptime}</div>
              </div>

              <div className="health-item">
                <div className={`health-icon ${getHealthStatusClass(systemHealth.storage.status)}`}>
                  {getHealthStatusIcon(systemHealth.storage.status)}
                </div>
                <div className="health-content">
                  <div className="health-label">Storage</div>
                  <div className="health-description">{systemHealth.storage.description}</div>
                </div>
                <div className="health-uptime">{systemHealth.storage.uptime}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .admin-overview {
          margin-bottom: 2rem;
        }

        .overview-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .overview-title {
          font-size: 1.75rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 0.5rem;
        }

        .overview-subtitle {
          color: var(--gray-600);
          font-size: 1rem;
        }

        .access-note {
          font-size: 0.875rem;
          color: var(--warning-yellow);
          font-style: italic;
        }

        .alert {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          text-align: center;
        }

        .metrics-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .summary-card {
          background: white;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-left: 4px solid var(--gray-300);
          transition: all 0.3s ease;
        }

        .summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .summary-card.primary { border-left-color: #007bff; }
        .summary-card.success { border-left-color: #28a745; }
        .summary-card.info { border-left-color: #8b5cf6; }

        .summary-content {
          flex: 1;
        }

        .summary-number {
          font-size: 2rem;
          font-weight: 700;
          color: var(--gray-800);
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        .summary-label {
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 0.25rem;
        }

        .summary-change {
          font-size: 0.875rem;
        }

        .summary-change.positive { color: #28a745; }
        .summary-change.negative { color: #dc3545; }
        .summary-change.neutral { color: #6c757d; }

        .summary-icon {
          font-size: 3rem;
          opacity: 0.8;
        }

        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .overview-card {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .overview-card.loading {
          padding: 2rem;
        }

        .loading-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          height: 200px;
          border-radius: 0.5rem;
        }

        .loading-skeleton-small {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          height: 40px;
          border-radius: 0.25rem;
          margin-bottom: 10px;
        }

        .loading-activity {
          padding: 1rem 0;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .card-header {
          padding: 1.5rem 2rem 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #f0f0f0;
        }

        .card-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--gray-800);
        }

        .activity-count,
        .stats-period {
          font-size: 0.875rem;
          color: #6c757d;
          background: #f8f9fa;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }

        .health-status {
          font-size: 0.875rem;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }

        .health-status.healthy {
          background: #d4edda;
          color: #28a745;
        }

        .card-body {
          padding: 1rem 2rem 2rem;
        }

        .activity-list {
          margin-bottom: 1rem;
        }

        .activity-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .activity-item:last-child {
          border-bottom: none;
        }

        .activity-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .activity-content {
          flex: 1;
        }

        .activity-description {
          font-weight: 500;
          color: var(--gray-800);
          margin-bottom: 0.25rem;
        }

        .activity-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
          color: #6c757d;
        }

        .activity-status {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .activity-footer {
          text-align: center;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #f0f0f0;
        }

        .no-activity {
          text-align: center;
          padding: 2rem;
          color: #6c757d;
        }

        .no-activity-icon {
          font-size: 2rem;
          display: block;
          margin-bottom: 0.5rem;
        }

        .quick-stats {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .quick-stat {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .quick-stat-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .quick-stat-content {
          flex: 1;
        }

        .quick-stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-800);
        }

        .quick-stat-label {
          font-weight: 500;
          color: var(--gray-700);
        }

        .quick-stat-total {
          font-size: 0.875rem;
          color: #6c757d;
        }

        .quick-stat-growth {
          font-size: 0.875rem;
          font-weight: 600;
          color: #28a745;
          background: #d4edda;
          padding: 0.25rem 0.5rem;
          border-radius: 0.5rem;
        }

        .performance-indicators {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .indicator {
          margin-bottom: 1rem;
        }

        .indicator-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .indicator-label {
          font-weight: 500;
          color: var(--gray-700);
        }

        .indicator-value {
          font-weight: 600;
          color: var(--gray-800);
        }

        .indicator-bar {
          width: 100%;
          height: 8px;
          background: #f0f0f0;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 0.25rem;
        }

        .indicator-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.8s ease;
        }

        .indicator-fill.excellent {
          background: linear-gradient(90deg, #28a745, #20c997);
        }

        .indicator-fill.good {
          background: linear-gradient(90deg, #ffc107, #fd7e14);
        }

        .indicator-fill.poor {
          background: linear-gradient(90deg, #dc3545, #e74c3c);
        }

        .indicator-description {
          font-size: 0.875rem;
          color: #6c757d;
        }

        .health-indicators {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .health-item {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .health-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .health-content {
          flex: 1;
        }

        .health-label {
          font-weight: 500;
          color: var(--gray-700);
        }


        .health-description {
          font-size: 0.875rem;
          color: var(--gray-500);
        }

        .health-uptime {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-800);
        }

        .no-data {
          text-align: center;
          padding: 3rem 2rem;
          background: white;
          border-radius: 1rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .no-data-icon {
          font-size: 3rem;
          display: block;
          margin-bottom: 1rem;
        }

        .no-data h3 {
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .no-data p {
          color: var(--gray-500);
        }

        @media (max-width: 768px) {
          .overview-grid {
            grid-template-columns: 1fr;
          }

          .metrics-summary {
            grid-template-columns: 1fr;
          }

          .activity-meta {
            flex-direction: column;
            gap: 0.25rem;
          }

          .quick-stat {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminOverview;